import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RowLink, ActionButton, TextField, useLoad, fmt, COUNTRY_OPTIONS, can,
} from "@/components/control/kit";

export const orgFields = [
  { name: "name", label: "Öffentlicher Name", required: true },
  { name: "city", label: "Ort", required: true },
  { name: "country", label: "Land", type: "select", options: COUNTRY_OPTIONS, required: true },
  { name: "description", label: "Beschreibung", type: "textarea" },
  { name: "website", label: "Website (https://…)", type: "url" },
  { name: "responsibility", label: "Verantwortliche Stelle" },
  { name: "internal_note", label: "Interne Notiz", type: "textarea" },
  { name: "submit_now", label: "Direkt zur Prüfung senden", type: "checkbox" },
];

export default function Organizations() {
  const { user } = useAuth();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const st = useLoad(`/organizations?${new URLSearchParams({ status, q })}`);
  const tabs = useTabs([
    { value: "all", label: "Alle" }, { value: "entwurf", label: "Entwurf" }, { value: "zur_pruefung", label: "Zur Prüfung" },
    { value: "rückfrage", label: "Rückfrage" }, { value: "freigegeben", label: "Freigegeben" }, { value: "abgelehnt", label: "Abgelehnt" },
  ], status);
  return (
    <div>
      <PageHeader title="Organisationen" subtitle="Partnerorganisationen erfassen, Angaben prüfen und Teams einladen." icon={Building2}
        actions={<>
          {can(user, "organizations:approve") && <Link to="/organisationen/ansprueche" data-testid="org-claims-link" className="rounded-lg border border-brand-purple/30 bg-white px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary">Ansprüche prüfen</Link>}
          <Link to="/organisationsrechte" data-testid="org-rights-link" className="rounded-lg border border-brand-purple/30 bg-white px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary">Organisationsrechte</Link>
          {st.data?.can.manage && (
          <ActionButton label="Organisation hinzufügen" icon={Plus} variant="default" testId="create-organization" fields={orgFields}
            page="neu" pageSubtitle="Erfasse öffentliche Organisationsangaben als Entwurf."
            onDone={st.reload} submitLabel="Speichern"
            onSubmit={async ({ submit_now, ...v }) => { await api.post("/organizations", { ...v, submit: submit_now }); return "Organisation gespeichert."; }} />
          )}
        </>} />
      <Tabbar tabs={tabs} onChange={setStatus} testId="org-status-tabs" />
      <div className="mb-5 max-w-sm"><TextField id="org-q" label="Name" value={q} onChange={setQ} testId="org-search" /></div>
      <TabPanel tabs={tabs} testId="org-status-tabs-panel">
      <Section title="Organisationen" testId="organizations-section">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="organizations-table">
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Ort</TableHead><TableHead>Land</TableHead><TableHead>Aktualisiert</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.organizations.length === 0 && <TableRow><TableCell colSpan={5}><Empty /></TableCell></TableRow>}
                  {d.organizations.map((o) => (
                    <TableRow key={o.id} data-testid={`org-row-${o.id}`}>
                      <TableCell><RowLink to={`/organisationen/${o.id}`} testId={`org-link-${o.id}`}>{o.name}</RowLink></TableCell>
                      <TableCell>{o.city || "–"}</TableCell><TableCell>{o.country || "–"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmt(o.updated_at || o.created_at)}</TableCell>
                      <TableCell><Status value={o.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      </TabPanel>
    </div>
  );
}
